/**
 * The Word side of the agent's document tools.
 *
 * The agent cannot touch the document itself: when it calls
 * `read_document_range`, `search_document` or `edit_document`, the run pauses
 * on an interrupt, the pane executes the request here with Office.js and
 * resumes the run with the result (see useWordToolInterrupts.ts). Shapes
 * mirror `mytender/agents_v2/tools/word_document.py`; keep them in step.
 *
 * Addressing: paragraphs by 0-based index in `body.paragraphs`, each edit
 * carrying the text the agent last saw there. An edit whose paragraph has
 * moved or changed is reported back as stale with the current text, and the
 * agent re-reads and retries, rather than writing over the wrong paragraph.
 */

import {
  deleteParagraphAsAuthoredRevision,
  deleteParagraphContentAsAuthoredRevision,
  insertParagraphAfterAsAuthoredRevision,
  ooxmlPackage,
  replaceParagraphAsAuthoredRevision,
  replaceWholeParagraphAsAuthoredRevision,
  revisionParagraphXml,
} from "./authoredRevision";
import { describeError } from "./errors";
import { type Block, parseMarkdown } from "./markdown";
import { mytenderUrl, openExternal } from "./openExternal";
import { recordAction } from "./journal";
import { blocksXml, tableAfterParagraphXml } from "./revisionOoxml";
import { type TableSummary, documentCapabilities, loadTableSpans } from "./snapshot";
import { inspectTableOoxml, stampTableDeleted } from "./tableRevision";
import { type ExtractInsert, insertExtract } from "./templateFrame";
import { withTrackingOff } from "./tracking";

export type ParagraphRecord = {
  index: number;
  text: string;
  style: string | null;
  /** The index of the table this paragraph is a cell of, when it is one. */
  in_table?: number;
};

export type SearchMatch = { index: number; text: string };

export type EditOpKind = "replace" | "insert_after" | "insert_before" | "delete" | "delete_table" | "replace_table";

export type EditOp = {
  op: EditOpKind;
  index: number;
  expected_text: string;
  new_text?: string;
};

export type EditOutcome = {
  op: EditOp["op"];
  index: number;
  status: "applied" | "stale" | "missing" | "error" | "in_table" | "has_revisions" | "merged_cells";
  current_text?: string;
  message?: string;
  /** For in_table: the table's index, for delete_table / replace_table. */
  table_index?: number;
};

export type EditResult = {
  outcomes: EditOutcome[];
  tracked_changes: boolean;
  /** How the edits landed; "authored" means revisions by mytender.io. */
  mode: EditMode;
  authored: boolean;
  /** Why the host refused mytender.io-authored revisions, when it did. */
  authored_error?: string;
  paragraph_count: number;
};

export type DocumentComment = {
  index: number;
  expected_text: string;
  comment: string;
  quote?: string;
};

export type CommentOutcome = {
  index: number;
  status: "added" | "stale" | "missing" | "unsupported" | "error";
  current_text?: string;
  message?: string;
};

export type CommentResult = {
  outcomes: CommentOutcome[];
  comments_supported: boolean;
};

/** The most paragraphs one read returns; the agent pages through longer spans. */
export const MAX_READ_PARAGRAPHS = 200;
export const MAX_SEARCH_MATCHES = 20;
/** How far from the expected index a moved paragraph is still recognised. */
const RELOCATE_WINDOW = 8;

const normalise = (text: string): string => text.replace(/\s+/g, " ").trim();

/**
 * Find the paragraph an op refers to: at its index when the text still
 * matches, else the nearest paragraph nearby with that text (something above
 * it was inserted or deleted since the agent read).
 * @param texts - Current paragraph texts.
 * @param index - The index the agent gave.
 * @param expected - The text the agent last saw there.
 * @returns The index to act on, or null when nothing nearby matches.
 */
export const locateParagraph = (texts: readonly string[], index: number, expected: string): number | null => {
  const want = normalise(expected);
  if (index >= 0 && index < texts.length && normalise(texts[index]) === want) return index;
  for (let distance = 1; distance <= RELOCATE_WINDOW; distance += 1) {
    for (const candidate of [index - distance, index + distance]) {
      if (candidate >= 0 && candidate < texts.length && normalise(texts[candidate]) === want) {
        return candidate;
      }
    }
  }
  return null;
};

/**
 * Order ops so that earlier ones cannot shift the indexes of later ones:
 * highest index first, and at the same index deletions/replacements before
 * insertions.
 * @param ops - The agent's ops.
 * @returns A new, ordered array.
 */
export const orderOps = (ops: readonly EditOp[]): EditOp[] =>
  [...ops].sort((a, b) => {
    if (a.index !== b.index) return b.index - a.index;
    const rank = (op: EditOp["op"]) =>
      op === "delete" || op === "replace" || op === "delete_table" || op === "replace_table"
        ? 0
        : op === "insert_after"
          ? 1
          : 2;
    return rank(a.op) - rank(b.op);
  });

const splitParagraphs = (text: string): string[] => text.replace(/\r\n/g, "\n").split("\n");

/**
 * Read a span of paragraphs.
 * @param start - First index (inclusive).
 * @param end - Last index (inclusive); clamped to the read cap.
 * @returns The paragraphs and the document's current paragraph count.
 */
export const readDocumentRange = async (
  start: number,
  end: number
): Promise<{ paragraphs: ParagraphRecord[]; tables: TableSummary[]; paragraph_count: number }> =>
  Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text,items/style,items/tableNestingLevel");
    await context.sync();
    const count = paragraphs.items.length;
    const first = Math.max(0, Math.min(start, count - 1));
    const last = Math.min(count - 1, Math.max(end, first), first + MAX_READ_PARAGRAPHS - 1);
    const spans = await loadTableSpans(context, paragraphs);
    const tableOf = (i: number) => spans.find((t) => i >= t.index && i <= t.end_index);
    const out: ParagraphRecord[] = [];
    for (let i = first; i <= last; i += 1) {
      const p = paragraphs.items[i];
      const table = tableOf(i);
      out.push({ index: i, text: p.text ?? "", style: p.style ?? null, ...(table && { in_table: table.index }) });
    }
    // The tables the span touches, each once.
    const tables = spans.filter((t) => t.end_index >= first && t.index <= last);
    return { paragraphs: out, tables, paragraph_count: count };
  });

/**
 * Find paragraphs containing a phrase (case-insensitive).
 * @param query - The phrase.
 * @returns Matching paragraphs, nearest the start of the document first.
 */
export const searchDocument = async (query: string): Promise<{ matches: SearchMatch[]; paragraph_count: number }> =>
  Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text");
    await context.sync();
    const needle = normalise(query).toLowerCase();
    const matches: SearchMatch[] = [];
    if (needle) {
      paragraphs.items.forEach((p, index) => {
        if (matches.length >= MAX_SEARCH_MATCHES) return;
        const text = p.text ?? "";
        if (normalise(text).toLowerCase().includes(needle)) matches.push({ index, text });
      });
    }
    return { matches, paragraph_count: paragraphs.items.length };
  });

/** How edits reach the document: authored OOXML revisions, or plain tracked changes. */
export type EditMode = "authored" | "tracked" | "direct";

const AUTHORED_PREF_KEY = "mytender_authored_revisions";
// Only "on" is remembered: a host that refused one write is tried again
// next time, since one refusal (a stale paragraph, a table) says nothing
// about the next edit, and a latch here once left every later edit under
// the user's name for good.
let authoredState: "untried" | "on" | "off" = (() => {
  try {
    return localStorage.getItem(AUTHORED_PREF_KEY) === "on" ? "on" : "untried";
  } catch {
    return "untried";
  }
})();
/** The host's reasons for refused authored writes in the last batch, for the strip. */
let lastAuthoredError: string | null = null;

export { describeError };
const setAuthoredState = (state: "on" | "off") => {
  authoredState = state;
  try {
    localStorage.setItem(AUTHORED_PREF_KEY, state);
  } catch {
    // preference only
  }
};
/** Record that this host just wrote mytender.io-authored revisions (a pull, a move). */
export const markAuthoredRevisionsWorking = (): void => {
  if (authoredState !== "on") setAuthoredState("on");
};

/** Whether this host has written mytender.io-authored revisions successfully. */
export const authoredRevisionsActive = (): boolean => authoredState === "on";

/**
 * Apply the agent's edits, as tracked changes where Word supports them.
 *
 * Preferred path: each op is written as OOXML that already carries its
 * revision marks, authored "mytender.io", with tracking switched off around
 * the write, so the user sees exactly who changed what and can accept or
 * reject each edit on its own. When the host refuses the OOXML, the rest of
 * that batch continues as ordinary tracked changes (authored by the Word
 * user). Either way the user's tracking setting is restored afterwards.
 * @param ops - The agent's ops.
 * @returns One outcome per op, in the agent's order.
 */
export const applyDocumentEdits = async (ops: readonly EditOp[]): Promise<EditResult> => {
  const canTrack = documentCapabilities().tracked_changes;
  const ordered = orderOps(ops);
  const outcomes = new Map<EditOp, EditOutcome>();
  let mode: EditMode = canTrack ? "tracked" : "direct";
  let remaining: EditOp[] = ordered;

  lastAuthoredError = null;
  const startedAt = new Date().toISOString();
  if (canTrack) {
    // Every edit is a mytender.io revision or nothing: one Word refuses is
    // reported as not applied, never written under the user's own name.
    mode = "authored";
    remaining = await applyAuthored(ordered, outcomes);
    remaining = [];
  }
  const count = remaining.length ? await applyTracked(remaining, outcomes, canTrack) : await paragraphCount();
  const applied = ops.filter((op) => outcomes.get(op)?.status === "applied").length;
  if (applied && mode === "authored") {
    await recordAction({
      kind: "edit",
      at: startedAt,
      summary: `${applied} document edit${applied === 1 ? "" : "s"}`,
    });
  }
  return {
    outcomes: ops.map((op) => outcomes.get(op) as EditOutcome),
    tracked_changes: canTrack,
    mode,
    authored: mode === "authored",
    ...(lastAuthoredError && { authored_error: lastAuthoredError }),
    paragraph_count: count,
  };
};

const paragraphCount = (): Promise<number> =>
  Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items");
    await context.sync();
    return paragraphs.items.length;
  });

const staleOutcome = (op: EditOp, texts: readonly string[]): EditOutcome => {
  const inRange = op.index >= 0 && op.index < texts.length;
  return {
    op: op.op,
    index: op.index,
    status: inRange ? "stale" : "missing",
    ...(inRange && { current_text: texts[op.index] }),
    message: inRange
      ? "The paragraph at this index no longer has the expected text; read it again before editing."
      : "No paragraph at this index.",
  };
};

/**
 * A paragraph already replaced or deleted by an earlier op in the same batch
 * (two ops relocated onto one paragraph, or one op relocated onto another's
 * target): the later op is stale, since the text it expected is gone.
 */
const claimedOutcome = (op: EditOp, target: number, texts: readonly string[]): EditOutcome => ({
  op: op.op,
  index: op.index,
  status: "stale",
  current_text: texts[target],
  message: "Another op in this batch already changed this paragraph; read it again before editing.",
});

const claims = (op: EditOp): boolean => op.op === "replace" || op.op === "delete";

/**
 * Write ops as authored revisions, one sync per op so a refusal is caught
 * before the next op. Returns the ops still to apply (all of them when the
 * host refuses the first).
 */
const applyAuthored = async (ordered: readonly EditOp[], outcomes: Map<EditOp, EditOutcome>): Promise<EditOp[]> =>
  Word.run(async (context) => {
    const document = context.document;
    document.load("changeTrackingMode");
    await context.sync();
    const previousMode = document.changeTrackingMode;
    if (previousMode !== Word.ChangeTrackingMode.off) {
      document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();
    }
    const restore = async () => {
      if (previousMode === Word.ChangeTrackingMode.off) return;
      try {
        document.changeTrackingMode = previousMode;
        await context.sync();
      } catch (error) {
        // The outcomes are worth more than the mode: report, don't throw.
        console.warn("Could not restore the change-tracking mode after authored edits", describeError(error));
      }
    };
    const refused: EditOp[] = [];
    try {
      const paragraphs = document.body.paragraphs;
      paragraphs.load("items/text,items/style,items/tableNestingLevel");
      await context.sync();
      const texts = paragraphs.items.map((p) => p.text ?? "");
      const claimed = new Set<number>();
      const spans = ordered.some((op) => op.op === "delete_table" || op.op === "replace_table" || op.op === "delete")
        ? await loadTableSpans(context, paragraphs)
        : [];

      for (let i = 0; i < ordered.length; i += 1) {
        const op = ordered[i];
        if (op.op === "delete_table" || op.op === "replace_table") {
          outcomes.set(op, await applyTableOp(context, op, spans, texts));
          continue;
        }
        const target = locateParagraph(texts, op.index, op.expected_text);
        if (target === null) {
          outcomes.set(op, staleOutcome(op, texts));
          continue;
        }
        if (claimed.has(target)) {
          outcomes.set(op, claimedOutcome(op, target, texts));
          continue;
        }
        if (op.op === "delete" && (paragraphs.items[target].tableNestingLevel ?? 0) > 0) {
          // A cell's paragraph cannot be deleted: Word keeps the cell. Deleting
          // every cell paragraph "applied" on 28 Sep and left the table intact.
          const table = spans.find((t) => target >= t.index && target <= t.end_index);
          outcomes.set(op, {
            op: op.op,
            index: target,
            status: "in_table",
            ...(table && { table_index: table.index }),
            message: table
              ? `This paragraph is a cell of the table at ${table.index}–${table.end_index}. Use delete_table (or replace_table) at index ${table.index} instead.`
              : "This paragraph is a table cell; delete the table with delete_table instead.",
          });
          continue;
        }
        if (claims(op)) claimed.add(target);
        const paragraph = paragraphs.items[target];
        const style = paragraph.style ?? null;
        const lines = splitParagraphs(op.new_text ?? "");
        // Markdown that is more than plain paragraphs (a table, a list, a
        // heading, a page break) lands as real Word structure: the same OOXML
        // the pull path writes, so the agent's tables are tables, not lines of
        // pipes (28 Sep). Plain text keeps the per-paragraph path and its style.
        const blocks = parseMarkdown(op.new_text ?? "");
        const rich = blocks.some((b) => b.kind !== "paragraph") && op.op !== "delete";
        const richXml = () => ooxmlPackage(blocksXml(blocks));
        try {
          if (rich && (op.op === "replace" || op.op === "insert_after" || op.op === "insert_before")) {
            const whole = paragraph.getRange(Word.RangeLocation.whole);
            if (op.op === "replace") {
              // The old paragraph is deleted (mark and all when Word allows,
              // content only inside a control) and the blocks go in after it.
              try {
                deleteParagraphAsAuthoredRevision(paragraph, texts[target], style);
                await context.sync();
              } catch {
                deleteParagraphContentAsAuthoredRevision(paragraph, texts[target]);
                await context.sync();
              }
              whole.insertOoxml(richXml(), Word.InsertLocation.after);
            } else if (op.op === "insert_after") {
              const block = paragraph.parentContentControlOrNullObject;
              block.load("isNullObject,tag");
              const next = paragraph.getNextOrNullObject();
              next.load("isNullObject");
              // eslint-disable-next-line office-addins/call-sync-before-read, office-addins/load-object-before-read
              const nextBlock = next.parentContentControlOrNullObject;
              nextBlock.load("isNullObject,tag");
              await context.sync();
              const inBlock = !block.isNullObject && /^mytender-q:/.test(block.tag ?? "");
              const lastInBlock =
                inBlock && (next.isNullObject || nextBlock.isNullObject || nextBlock.tag !== block.tag);
              if (lastInBlock) block.insertOoxml(richXml(), Word.InsertLocation.end);
              else whole.insertOoxml(richXml(), Word.InsertLocation.after);
            } else {
              whole.insertOoxml(richXml(), Word.InsertLocation.before);
            }
            await context.sync();
            outcomes.set(op, { op: op.op, index: target, status: "applied" });
            if (authoredState !== "on") setAuthoredState("on");
            continue;
          }
          switch (op.op) {
            case "replace":
              try {
                replaceWholeParagraphAsAuthoredRevision(paragraph, texts[target], lines.join("\n"), style);
                await context.sync();
              } catch (first) {
                // Refused whole: the content-only form may still take. Both
                // refusals are reported if it doesn't.
                const reason = describeError(first);
                try {
                  replaceParagraphAsAuthoredRevision(paragraph, texts[target], lines.join("\n"), style);
                  await context.sync();
                } catch (second) {
                  throw new Error(`whole paragraph: ${reason}; content: ${describeError(second)}`);
                }
              }
              break;
            case "delete":
              deleteParagraphAsAuthoredRevision(paragraph, texts[target], style);
              break;
            case "insert_after": {
              // After the last paragraph of a question's answer block, "after"
              // would land outside the block (and so outside the answer): those
              // go in at the block's end instead.
              const block = paragraph.parentContentControlOrNullObject;
              block.load("isNullObject,tag");
              const next = paragraph.getNextOrNullObject();
              next.load("isNullObject");
              // Navigation properties are proxies resolved at sync; the rule can't see that.
              // eslint-disable-next-line office-addins/call-sync-before-read, office-addins/load-object-before-read
              const nextBlock = next.parentContentControlOrNullObject;
              nextBlock.load("isNullObject,tag");
              await context.sync();
              const inBlock = !block.isNullObject && /^mytender-q:/.test(block.tag ?? "");
              const lastInBlock =
                inBlock && (next.isNullObject || nextBlock.isNullObject || nextBlock.tag !== block.tag);
              if (lastInBlock) {
                for (const line of lines)
                  block.insertOoxml(ooxmlPackage(revisionParagraphXml("", line, null)), Word.InsertLocation.end);
              } else {
                // Inserted after in reverse so the lines end up in order.
                for (const line of [...lines].reverse()) insertParagraphAfterAsAuthoredRevision(paragraph, line, null);
              }
              break;
            }
            case "insert_before": {
              const before = paragraph.getPreviousOrNullObject();
              before.load("isNullObject");
              await context.sync();
              if (before.isNullObject) {
                for (const line of lines) {
                  const xml = ooxmlPackage(revisionParagraphXml("", line, null));
                  document.body.insertOoxml(xml, Word.InsertLocation.start);
                }
              } else {
                for (const line of [...lines].reverse()) insertParagraphAfterAsAuthoredRevision(before, line, null);
              }
              break;
            }
          }
          await context.sync();
          outcomes.set(op, { op: op.op, index: target, status: "applied" });
          if (authoredState !== "on") setAuthoredState("on");
        } catch (error) {
          // The host refused the OOXML for this op: it is not applied (it must
          // not land under the user's name); the rest of the batch continues.
          console.warn("Authored revision refused; the edit is not applied", error);
          const reason = error instanceof Error ? error.message : describeError(error);
          lastAuthoredError = lastAuthoredError ? `${lastAuthoredError}; ${reason}` : reason;
          outcomes.set(op, {
            op: op.op,
            index: target,
            status: "error",
            message: `Word refused this change as a mytender.io revision, so it was not applied (${reason}).`,
          });
          refused.push(op);
        }
      }
    } finally {
      // Tracking was switched off for the writes: it comes back whatever
      // failed, including a load outside the per-op try.
      await restore();
    }
    if (refused.length) lastAuthoredError = `${refused.length} of ${ordered.length} refused: ${lastAuthoredError}`;
    return refused;
  });

type TableBlock = Extract<Block, { kind: "table" }>;

const normaliseRow = (cells: readonly string[]): string =>
  cells
    .map((c) => normalise(c))
    .join(" | ")
    .toLowerCase();

/**
 * Delete or replace a whole table as a mytender.io revision. Runs inside
 * applyAuthored's batch (tracking is already off).
 */
const applyTableOp = async (
  context: Word.RequestContext,
  op: EditOp,
  spans: readonly TableSummary[],
  texts: readonly string[]
): Promise<EditOutcome> => {
  const k =
    spans.findIndex((t) => op.index === t.index) >= 0
      ? spans.findIndex((t) => op.index === t.index)
      : spans.findIndex((t) => op.index >= t.index && op.index <= t.end_index);
  if (k < 0) {
    const inRange = op.index >= 0 && op.index < texts.length;
    return {
      op: op.op,
      index: op.index,
      status: "missing",
      message: inRange ? "No table at this index." : "No paragraph at this index.",
    };
  }
  const span = spans[k];
  const firstRow = span.first_row.join(" | ");
  const rowIsEmpty = span.first_row.every((cell) => !cell.trim());
  const expectedIsEmpty = !op.expected_text.replace(/\|/g, "").trim();
  if (!(rowIsEmpty && expectedIsEmpty) && normaliseRow(span.first_row) !== normaliseRow(op.expected_text.split("|"))) {
    return {
      op: op.op,
      index: span.index,
      status: "stale",
      current_text: firstRow,
      table_index: span.index,
      message: "The table's first row no longer matches expected_text; read it again before editing.",
    };
  }
  let newTable: { rows: TableBlock["rows"]; header: boolean } | null = null;
  if (op.op === "replace_table") {
    const block = parseMarkdown(op.new_text ?? "").find((b) => b.kind === "table");
    if (!block || block.kind !== "table" || !block.rows.length) {
      return {
        op: op.op,
        index: span.index,
        status: "error",
        table_index: span.index,
        message: "new_text holds no markdown table.",
      };
    }
    newTable = { rows: block.rows, header: block.header };
  }
  const tables = context.document.body.tables;
  tables.load("items");
  await context.sync();
  const table = tables.items[k];
  if (!table) return { op: op.op, index: span.index, status: "missing", message: "The table could not be found." };
  const whole = table.getRange(Word.RangeLocation.whole);
  const ooxml = whole.getOoxml();
  await context.sync();
  // A ClientResult is read after the sync; the rule expects a load() it has no use for.
  // eslint-disable-next-line office-addins/load-object-before-read
  const info = inspectTableOoxml(ooxml.value);
  if (op.op === "replace_table" && info.mergedCells) {
    return {
      op: op.op,
      index: span.index,
      status: "merged_cells",
      table_index: span.index,
      message:
        "This table has merged cells, which a replacement cannot reproduce. Edit its cells in place, or ask the user.",
    };
  }
  if (info.revisionAuthors.length && !info.onlyOurInsertions) {
    return {
      op: op.op,
      index: span.index,
      status: "has_revisions",
      table_index: span.index,
      message: `This table already carries tracked changes by ${info.revisionAuthors.join(", ")}. Ask the user to accept or reject them first.`,
    };
  }
  try {
    if (info.onlyOurInsertions) {
      // Withdrawing our own suggestion: the table simply goes.
      table.delete();
    } else {
      // eslint-disable-next-line office-addins/load-object-before-read
      whole.insertOoxml(stampTableDeleted(ooxml.value), Word.InsertLocation.replace);
    }
    await context.sync();
    if (newTable) {
      if (info.onlyOurInsertions) {
        // The table is gone; the new one goes where its first cell paragraph was.
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items");
        await context.sync();
        const anchor = paragraphs.items[Math.max(0, span.index - 1)];
        const xml = ooxmlPackage(tableAfterParagraphXml(newTable.rows, newTable.header));
        if (anchor) anchor.getRange(Word.RangeLocation.whole).insertOoxml(xml, Word.InsertLocation.after);
        else context.document.body.insertOoxml(xml, Word.InsertLocation.start);
      } else {
        const xml = ooxmlPackage(tableAfterParagraphXml(newTable.rows, newTable.header));
        table.getRange(Word.RangeLocation.whole).insertOoxml(xml, Word.InsertLocation.after);
      }
      await context.sync();
    }
    if (authoredState !== "on") setAuthoredState("on");
    return { op: op.op, index: span.index, status: "applied", table_index: span.index };
  } catch (error) {
    const reason = describeError(error);
    lastAuthoredError = lastAuthoredError ? `${lastAuthoredError}; ${reason}` : reason;
    return {
      op: op.op,
      index: span.index,
      status: "error",
      table_index: span.index,
      message: `Word refused this table change as a mytender.io revision, so it was not applied (${reason}).`,
    };
  }
};

/** Write ops with Word's own change tracking (or directly when it has none). */
const applyTracked = async (
  ordered: readonly EditOp[],
  outcomes: Map<EditOp, EditOutcome>,
  canTrack: boolean
): Promise<number> =>
  Word.run(async (context) => {
    const document = context.document;
    let previousMode: Word.ChangeTrackingMode | `${Word.ChangeTrackingMode}` | null = null;
    if (canTrack) {
      document.load("changeTrackingMode");
      await context.sync();
      previousMode = document.changeTrackingMode;
      if (previousMode !== Word.ChangeTrackingMode.trackAll) {
        document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      }
    }
    // Office.js reports a queued op's failure at sync(), outside the per-op
    // try/catch, so the user's tracking mode is restored on every exit.
    const restore = async () => {
      if (canTrack && previousMode !== null && previousMode !== Word.ChangeTrackingMode.trackAll) {
        document.changeTrackingMode = previousMode;
        await context.sync();
      }
    };

    const paragraphs = document.body.paragraphs;
    paragraphs.load("items/text");
    await context.sync();
    const texts = paragraphs.items.map((p) => p.text ?? "");
    const claimed = new Set<number>();

    try {
      for (const op of ordered) {
        const target = locateParagraph(texts, op.index, op.expected_text);
        if (target === null) {
          outcomes.set(op, staleOutcome(op, texts));
          continue;
        }
        if (claimed.has(target)) {
          outcomes.set(op, claimedOutcome(op, target, texts));
          continue;
        }
        if (claims(op)) claimed.add(target);
        const paragraph = paragraphs.items[target];
        const lines = splitParagraphs(op.new_text ?? "");
        try {
          switch (op.op) {
            case "replace":
              paragraph.insertText(lines.join("\n"), Word.InsertLocation.replace);
              break;
            case "delete":
              paragraph.delete();
              break;
            case "insert_after": {
              let anchor = paragraph;
              for (const line of lines) anchor = anchor.insertParagraph(line, Word.InsertLocation.after);
              break;
            }
            case "insert_before": {
              for (const line of lines) paragraph.insertParagraph(line, Word.InsertLocation.before);
              break;
            }
          }
          outcomes.set(op, { op: op.op, index: target, status: "applied" });
        } catch (error) {
          outcomes.set(op, {
            op: op.op,
            index: target,
            status: "error",
            message: error instanceof Error ? error.message : String(error),
          });
        }
      }
      await context.sync();
    } finally {
      await restore();
    }
    paragraphs.load("items");
    await context.sync();
    return paragraphs.items.length;
  });

/**
 * Leave review comments in the document, each on its paragraph or on a phrase
 * inside it. Comments need WordApi 1.4; without it every comment comes back
 * unsupported and the agent lists the findings in its reply instead.
 * @param comments - The agent's comments.
 * @returns One outcome per comment, in the agent's order.
 */
export const addDocumentComments = async (comments: readonly DocumentComment[]): Promise<CommentResult> => {
  if (!documentCapabilities().comments) {
    return {
      outcomes: comments.map((c) => ({ index: c.index, status: "unsupported" as const })),
      comments_supported: false,
    };
  }
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text");
    await context.sync();
    const texts = paragraphs.items.map((p) => p.text ?? "");

    // Resolve anchors first (one sync for every phrase search), then comment.
    const plans = comments.map((c) => {
      const target = locateParagraph(texts, c.index, c.expected_text);
      if (target === null) return { c, target: null as number | null, search: null as Word.RangeCollection | null };
      const quote = c.quote?.trim();
      const search = quote ? paragraphs.items[target].search(quote, { matchCase: false }) : null;
      if (search) search.load("items");
      return { c, target, search };
    });
    await context.sync();

    const outcomes: CommentOutcome[] = [];
    for (const { c, target, search } of plans) {
      if (target === null) {
        const inRange = c.index >= 0 && c.index < texts.length;
        outcomes.push({
          index: c.index,
          status: inRange ? "stale" : "missing",
          ...(inRange && { current_text: texts[c.index] }),
        });
        continue;
      }
      try {
        const range = search && search.items.length > 0 ? search.items[0] : paragraphs.items[target].getRange();
        range.insertComment(c.comment);
        outcomes.push({ index: target, status: "added" });
      } catch (error) {
        outcomes.push({
          index: target,
          status: "error",
          message: error instanceof Error ? error.message : String(error),
        });
      }
    }
    await context.sync();
    return { outcomes, comments_supported: true };
  });
};

export type ImageInsert = {
  index: number;
  expected_text: string;
  position: "after" | "before";
  alt: string | null;
  /** A data: URL from the generator. */
  image: string;
};

export type ImageOutcome = {
  index: number;
  status: "inserted" | "stale" | "missing" | "error";
  current_text?: string;
  message?: string;
};

const dataUrlBase64 = (dataUrl: string): string | null => {
  const match = /^data:image\/[a-z0-9.+-]+;base64,(.+)$/i.exec(dataUrl.trim());
  return match ? match[1] : null;
};

/**
 * Insert a generated image next to a paragraph, as a tracked change where
 * Word supports it.
 * @param insert - The image and its anchor.
 * @returns The outcome.
 */
export const insertDocumentImage = async (insert: ImageInsert): Promise<{ outcome: ImageOutcome }> => {
  const base64 = dataUrlBase64(insert.image);
  if (!base64) return { outcome: { index: insert.index, status: "error", message: "The image was not a data URL." } };
  const canTrack = documentCapabilities().tracked_changes;
  return Word.run(async (context) => {
    const document = context.document;
    let previousMode: Word.ChangeTrackingMode | `${Word.ChangeTrackingMode}` | null = null;
    if (canTrack) {
      document.load("changeTrackingMode");
      await context.sync();
      previousMode = document.changeTrackingMode;
      if (previousMode !== Word.ChangeTrackingMode.trackAll)
        document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    }
    const restore = async () => {
      if (canTrack && previousMode !== null && previousMode !== Word.ChangeTrackingMode.trackAll) {
        document.changeTrackingMode = previousMode;
        await context.sync();
      }
    };
    const paragraphs = document.body.paragraphs;
    paragraphs.load("items/text");
    await context.sync();
    const texts = paragraphs.items.map((p) => p.text ?? "");
    const target = locateParagraph(texts, insert.index, insert.expected_text);
    let outcome: ImageOutcome;
    try {
      if (target === null) {
        const inRange = insert.index >= 0 && insert.index < texts.length;
        outcome = {
          index: insert.index,
          status: inRange ? "stale" : "missing",
          ...(inRange && { current_text: texts[insert.index] }),
        };
      } else {
        try {
          // A picture can only go at the start/end of a paragraph, so the image
          // gets a paragraph of its own next to the anchor.
          const anchor = paragraphs.items[target];
          const holder = anchor.insertParagraph(
            "",
            insert.position === "before" ? Word.InsertLocation.before : Word.InsertLocation.after
          );
          const picture = holder.insertInlinePictureFromBase64(base64, Word.InsertLocation.start);
          if (insert.alt) picture.altTextDescription = insert.alt;
          outcome = { index: target, status: "inserted" };
        } catch (error) {
          outcome = { index: target, status: "error", message: error instanceof Error ? error.message : String(error) };
        }
      }
      await context.sync();
    } finally {
      await restore();
    }
    return { outcome };
  });
};

/**
 * Open a bid page in the mytender web app.
 * @param bidId - The linked bid, or null.
 * @param view - The web app tab id.
 * @returns What the agent is told.
 */
export const openInMytender = (bidId: string | null, view: string | null): { opened: boolean; url: string } => {
  const url = mytenderUrl(bidId, view);
  openExternal(url);
  return { opened: true, url };
};

export type TemplatePagesInsert = {
  docx_base64: string;
  /** The same pages whole as a flat OOXML package, the last try if Word refuses the file. */
  ooxml?: string;
  position: "start" | "end" | "after_paragraph";
  index?: number;
  expected_text?: string;
};

/**
 * Insert an extract of a template (its chosen pages, built server-side) into
 * the document. Not tracked: a frame page is layout, not a change to the
 * user's words, and Undo reverses it. Pages at the start or end are marked
 * as the template's frame so the next template can swap them (the untagged
 * retry path is how a second cover crept in on 28 Sep).
 * @param insert - The extract and where it goes.
 * @returns The outcome.
 */
export const insertTemplatePages = async (
  insert: TemplatePagesInsert
): Promise<{
  status: "inserted" | "stale" | "missing" | "error";
  message?: string;
  current_text?: string;
  tracked_changes: boolean;
  tagged: boolean;
  /** Which form went in: "full" (the file), "ooxml" (the same pages as a package), or "none". */
  used: string;
  refusals: string[];
}> =>
  withTrackingOff(async () => {
    let where: ExtractInsert["where"];
    if (insert.position === "after_paragraph") {
      const located = await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items/text");
        await context.sync();
        const texts = paragraphs.items.map((p) => p.text ?? "");
        return { texts, target: locateParagraph(texts, insert.index ?? -1, insert.expected_text ?? "") };
      });
      if (located.target === null) {
        const inRange = (insert.index ?? -1) >= 0 && (insert.index ?? -1) < located.texts.length;
        return {
          status: inRange ? "stale" : "missing",
          ...(inRange && { current_text: located.texts[insert.index as number] }),
          tracked_changes: false,
          tagged: false,
          used: "none",
          refusals: [],
        };
      }
      where = { after: located.target };
    } else {
      where = insert.position;
    }
    const outcome = await insertExtract({
      docx_base64: insert.docx_base64,
      ooxml: insert.ooxml,
      where,
      label: insert.position === "after_paragraph" ? null : "Template pages",
    });
    return {
      status: outcome.status,
      ...(outcome.message && { message: outcome.message }),
      tracked_changes: false,
      tagged: outcome.tagged,
      used: outcome.used,
      refusals: outcome.refusals,
    };
  });

export type HeadersFootersApply = { header_ooxml: string | null; footer_ooxml: string | null };

/**
 * Replace the main header and/or footer of every section with a template's.
 * @param apply - The header and footer bodies as flat OPC packages.
 * @returns What was replaced.
 */
export const applyTemplateHeadersFooters = async (
  apply: HeadersFootersApply
): Promise<{ header: boolean; footer: boolean; sections: number; message?: string }> =>
  withTrackingOff(() =>
    Word.run(async (context) => {
      const sections = context.document.sections;
      sections.load("items");
      await context.sync();
      let header = false;
      let footer = false;
      for (const section of sections.items) {
        if (apply.header_ooxml) {
          section.getHeader(Word.HeaderFooterType.primary).insertOoxml(apply.header_ooxml, Word.InsertLocation.replace);
          header = true;
        }
        if (apply.footer_ooxml) {
          section.getFooter(Word.HeaderFooterType.primary).insertOoxml(apply.footer_ooxml, Word.InsertLocation.replace);
          footer = true;
        }
      }
      await context.sync();
      return { header, footer, sections: sections.items.length };
    })
  );
